import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import classNames from "classnames";
import { UXPContext } from "@core/context/Context";
import Modal from "@widget/popups/Modal";
import SearchBox from "@widget/forms/inputs/SearchBox";
import Icon from "@icon/Icon";
import useDebounce from "@hooks/useDebounce";
import ToggleFilter from "@widget/forms/inputs/ToggleFilter";
import Button from "@widget/buttons/Button";
import PaginationComponent from "@widget/list/Pagination/PaginationComponent";
import { copyToClipboard } from "@utils";
import useToast from "@notifications/Toast/useToast";
const IconPickerModal = (props) => {
    const { show, onClose, onSelect, currentValue, defaultViewMode = 'compact' } = props;
    const context = React.useContext(UXPContext);
    const toast = useToast();
    const [searchQuery, setSearchQuery] = React.useState("");
    const [allFilteredIcons, setAllFilteredIcons] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    // Pagination state
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(50);
    // Sidebar state
    const [selectedIcon, setSelectedIcon] = React.useState(null);
    const [selectedPackage, setSelectedPackage] = React.useState(null);
    const [selectedWeight, setSelectedWeight] = React.useState(null);
    const [showSidePanel, setShowSidePanel] = React.useState(false);
    const [sidebarViewMode, setSidebarViewMode] = React.useState(defaultViewMode);
    const debouncedQuery = useDebounce(searchQuery, 300);
    const IconMetadata = React.useMemo(() => {
        return (typeof window !== 'undefined' && window.IconMetadata);
    }, []);
    // Search icons when debounced query changes - sort alphabetically
    React.useEffect(() => {
        if (!IconMetadata || !show)
            return;
        setIsLoading(true);
        try {
            const results = IconMetadata.searchIcons(debouncedQuery);
            // Sort alphabetically by displayName
            const sortedResults = results.sort((a, b) => a.displayName.localeCompare(b.displayName));
            setAllFilteredIcons(sortedResults);
            setPage(1); // Reset to first page on new search
        }
        catch (error) {
            console.error('Error searching icons:', error);
            setAllFilteredIcons([]);
        }
        finally {
            setIsLoading(false);
        }
    }, [debouncedQuery, IconMetadata, show]);
    // Calculate paginated icons
    const paginatedIcons = React.useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return allFilteredIcons.slice(startIndex, endIndex);
    }, [allFilteredIcons, page, pageSize]);
    // Reset sidebar when closing
    React.useEffect(() => {
        if (!show) {
            setShowSidePanel(false);
            setSelectedIcon(null);
            setSearchQuery("");
            setPage(1);
        }
    }, [show]);
    // Handle escape key
    React.useEffect(() => {
        if (!show)
            return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (showSidePanel) {
                    setShowSidePanel(false);
                }
                else {
                    onClose();
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [show, showSidePanel, onClose]);
    // Get first available variant for an icon
    const getFirstAvailableVariant = (icon) => {
        if (!IconMetadata)
            return null;
        // Prefer FA solid, fallback to first available
        if (icon.packages.fa) {
            const weight = icon.packages.fa.includes('solid') ? 'solid' : icon.packages.fa[0];
            return IconMetadata.buildIconString('fa', weight, icon.name);
        }
        if (icon.packages.ph) {
            const weight = icon.packages.ph.includes('regular') ? 'regular' : icon.packages.ph[0];
            return IconMetadata.buildIconString('ph', weight, icon.name);
        }
        return null;
    };
    // Handle icon click - open sidebar
    const handleIconClick = (icon) => {
        setSelectedIcon(icon);
        // Determine default package and weight
        if (icon.packages.fa && icon.packages.ph) {
            // Both available - prefer FA
            setSelectedPackage('fa');
            setSelectedWeight(icon.packages.fa.includes('solid') ? 'solid' : icon.packages.fa[0]);
        }
        else if (icon.packages.fa) {
            setSelectedPackage('fa');
            setSelectedWeight(icon.packages.fa.includes('solid') ? 'solid' : icon.packages.fa[0]);
        }
        else if (icon.packages.ph) {
            setSelectedPackage('ph');
            setSelectedWeight(icon.packages.ph.includes('regular') ? 'regular' : icon.packages.ph[0]);
        }
        setShowSidePanel(true);
    };
    // Handle variant click - update selected package and weight
    const handleVariantClick = (pkg, weight) => {
        setSelectedPackage(pkg);
        setSelectedWeight(weight);
    };
    // Handle confirm in side panel
    const handleConfirmSelection = () => {
        if (!selectedIcon || !selectedPackage || !selectedWeight || !IconMetadata)
            return;
        const iconString = IconMetadata.buildIconString(selectedPackage, selectedWeight, selectedIcon.name);
        if (iconString) {
            onSelect(iconString);
            setShowSidePanel(false);
        }
    };
    // Handle copy icon code to clipboard
    const handleCopyIconCode = () => {
        if (!selectedIcon || !selectedPackage || !selectedWeight || !IconMetadata)
            return;
        const iconString = IconMetadata.buildIconString(selectedPackage, selectedWeight, selectedIcon.name);
        if (iconString) {
            copyToClipboard(iconString);
            toast.success(context.$L("uxp-core.icon-picker.copied-to-clipboard"));
        }
    };
    // Get available weights for selected package (for ToggleFilter)
    const getAvailableWeights = () => {
        if (!selectedIcon || !selectedPackage)
            return [];
        const weights = selectedPackage === 'fa' ? selectedIcon.packages.fa : selectedIcon.packages.ph;
        if (!weights)
            return [];
        return weights.map(weight => ({
            label: weight.charAt(0).toUpperCase() + weight.slice(1),
            value: weight
        }));
    };
    // Get all icon variants for expanded view
    const getAllVariants = () => {
        if (!selectedIcon || !IconMetadata)
            return [];
        const variants = [];
        if (selectedIcon.packages.fa) {
            selectedIcon.packages.fa.forEach(weight => {
                const iconString = IconMetadata.buildIconString('fa', weight, selectedIcon.name);
                if (iconString) {
                    variants.push({ iconString, label: `FA ${weight}`, package: 'fa', weight });
                }
            });
        }
        if (selectedIcon.packages.ph) {
            selectedIcon.packages.ph.forEach(weight => {
                const iconString = IconMetadata.buildIconString('ph', weight, selectedIcon.name);
                if (iconString) {
                    variants.push({ iconString, label: `PH ${weight}`, package: 'ph', weight });
                }
            });
        }
        return variants;
    };
    // Build preview icon string
    const buildPreviewIcon = () => {
        if (!selectedIcon || !selectedPackage || !selectedWeight || !IconMetadata)
            return null;
        return IconMetadata.buildIconString(selectedPackage, selectedWeight, selectedIcon.name);
    };
    // Render icon grid
    const renderIconGrid = () => {
        return (_jsx("div", { className: "uxpcore_icon-picker-modal__icon-grid", children: paginatedIcons.map((icon, idx) => {
                const firstVariant = getFirstAvailableVariant(icon);
                if (!firstVariant)
                    return null;
                const isSelected = (selectedIcon === null || selectedIcon === void 0 ? void 0 : selectedIcon.name) === icon.name;
                return (_jsxs("div", { className: classNames("uxpcore_icon-picker-modal__icon-card", {
                        "uxpcore_icon-picker-modal__icon-card--selected": isSelected
                    }), onClick: () => handleIconClick(icon), title: icon.displayName, children: [_jsx("div", { className: "uxpcore_icon-picker-modal__icon-card-icon", children: _jsx(Icon, { icon: firstVariant, size: "2x" }) }), _jsx("div", { className: "uxpcore_icon-picker-modal__icon-card-name line-clamp-2", children: icon.displayName })] }, idx));
            }) }));
    };
    // Render side panel
    const renderSidePanel = () => {
        if (!selectedIcon)
            return null;
        const previewIcon = buildPreviewIcon() || getFirstAvailableVariant(selectedIcon);
        const allVariants = getAllVariants();
        const packageOptions = [];
        if (selectedIcon.packages.fa)
            packageOptions.push({ label: 'FontAwesome', value: 'fa' });
        if (selectedIcon.packages.ph)
            packageOptions.push({ label: 'Phosphor', value: 'ph' });
        return (_jsxs("div", { className: classNames("uxpcore_icon-picker-modal__side-panel", { "uxpcore_icon-picker-modal__side-panel--open": showSidePanel }), children: [_jsxs("div", { className: "uxpcore_icon-picker-modal__side-panel-header", children: [_jsx("div", { className: "uxpcore_icon-picker-modal__side-panel-title", children: selectedIcon.displayName }), _jsx("div", { className: "uxpcore_icon-picker-modal__side-panel-viewchanger", onClick: () => {
                                setSidebarViewMode(prev => (prev === 'compact' ? 'expanded' : 'compact'));
                            }, children: _jsx(Icon, { icon: sidebarViewMode == 'compact' ? ['phl', 'squares-four'] : ['phl', 'sliders'] }) })] }), _jsxs("div", { className: "uxpcore_icon-picker-modal__side-panel-content", children: [sidebarViewMode === 'compact' && (_jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_icon-picker-modal__side-panel-preview", children: previewIcon && _jsx(Icon, { icon: previewIcon, size: "5x", style: { width: '100%', height: '100%' } }) }), _jsxs("div", { className: "uxpcore_icon-picker-modal__side-panel-options", children: [packageOptions.length > 1 && (_jsxs("div", { className: "uxpcore_icon-picker-modal__side-panel-section", children: [_jsx("div", { className: "uxpcore_icon-picker-modal__side-panel-section-label", children: context.$L("uxp-core.icon-picker.package-label") }), _jsx(ToggleFilter, { options: packageOptions, value: selectedPackage || '', onChange: (pkg) => {
                                                        setSelectedPackage(pkg);
                                                        // Reset weight to first available for new package
                                                        const newWeights = pkg === 'fa' ? selectedIcon.packages.fa : selectedIcon.packages.ph;
                                                        if (newWeights) {
                                                            setSelectedWeight(newWeights[0]);
                                                        }
                                                    } })] })), selectedPackage && getAvailableWeights().length > 0 && (_jsxs("div", { className: "uxpcore_icon-picker-modal__side-panel-section", children: [_jsx("div", { className: "uxpcore_icon-picker-modal__side-panel-section-label", children: context.$L("uxp-core.icon-picker.weight-label") }), _jsx(ToggleFilter, { options: getAvailableWeights(), value: selectedWeight || '', onChange: (weight) => setSelectedWeight(weight) })] }))] })] })), sidebarViewMode === 'expanded' && (_jsx("div", { className: "uxpcore_icon-picker-modal__side-panel-variants", children: allVariants.map((variant, idx) => (_jsxs("div", { className: classNames("uxpcore_icon-picker-modal__variant-card", {
                                    "uxpcore_icon-picker-modal__variant-card--selected": selectedPackage === variant.package && selectedWeight === variant.weight
                                }), onClick: () => handleVariantClick(variant.package, variant.weight), title: variant.iconString, children: [_jsx("div", { className: "uxpcore_icon-picker-modal__variant-card-icon", children: _jsx(Icon, { icon: variant.iconString, size: "2x" }) }), _jsx("div", { className: "uxpcore_icon-picker-modal__variant-card-label", children: variant.label })] }, idx))) })), _jsxs("div", { className: "uxpcore_icon-picker-modal__side-panel-info", children: [_jsxs("div", { className: "uxpcore_icon-picker-modal__side-panel-icon-code", onClick: handleCopyIconCode, title: context.$L("uxp-core.icon-picker.click-to-copy"), children: [_jsx("div", { className: "uxpcore_icon-picker-modal__side-panel-icon-code-label", children: context.$L("uxp-core.icon-picker.icon-code-label") }), _jsxs("div", { className: "uxpcore_icon-picker-modal__side-panel-icon-code-value", children: [selectedPackage && selectedWeight && IconMetadata &&
                                                    IconMetadata.buildIconString(selectedPackage, selectedWeight, selectedIcon.name), _jsx(Icon, { icon: "phl:copy", size: "sm" })] })] }), selectedIcon.tags && selectedIcon.tags.length > 0 && (_jsxs("div", { className: "uxpcore_icon-picker-modal__side-panel-tags", children: [_jsx("div", { className: "uxpcore_icon-picker-modal__side-panel-tags-label", children: context.$L("uxp-core.icon-picker.tags-label") }), _jsx("div", { className: "uxpcore_icon-picker-modal__side-panel-tags-list", children: selectedIcon.tags.map((tag, idx) => (_jsx("span", { className: "uxpcore_icon-picker-modal__side-panel-tag", children: tag }, idx))) })] }))] })] }), _jsxs("div", { className: "uxpcore_icon-picker-modal__side-panel-actions", children: [_jsx(Button, { title: context.$L("uxp-core.icon-picker.confirm"), variant: "primary", onClick: handleConfirmSelection, disabled: !selectedPackage || !selectedWeight, leftIcon: "phl:check", className: "uxpcore_icon-picker-modal__confirm-btn" }), _jsx("div", { className: "uxpcore_icon-picker-modal__close-btn", onClick: () => setShowSidePanel(false), title: context.$L("uxp-core.icon-picker.cancel"), children: _jsx(Icon, { icon: "phl:x", size: "lg" }) })] })] }));
    };
    // Custom header with SearchBox
    const customHeader = (_jsxs("div", { className: "uxpcore_icon-picker-modal__custom-header", children: [_jsx("div", { className: "uxpcore_icon-picker-modal__title", children: context.$L("uxp-core.icon-picker.select-icon") }), _jsx("div", { className: "uxpcore_icon-picker-modal__search", children: _jsx(SearchBox, { value: searchQuery, onChange: setSearchQuery, placeholder: context.$L("uxp-core.icon-picker.search-icons") }) })] }));
    return (_jsx(Modal, { show: show, onClose: onClose, headerContent: customHeader, className: "uxpcore_icon-picker-modal", backgroundDismiss: !showSidePanel, showCloseButton: true, children: _jsxs("div", { className: classNames("uxpcore_icon-picker-modal__content", { "uxpcore_icon-picker-modal__content--sidebar-open": showSidePanel }), children: [_jsxs("div", { className: "uxpcore_icon-picker-modal__main", children: [_jsx("div", { className: "uxpcore_icon-picker-modal__results", children: isLoading ? (_jsx("div", { className: "uxpcore_icon-picker-modal__loading", children: context.$L("uxp-core.icon-picker.loading") })) : allFilteredIcons.length === 0 ? (_jsx("div", { className: "uxpcore_icon-picker-modal__empty", children: context.$L("uxp-core.icon-picker.no-results") })) : (renderIconGrid()) }), !isLoading && allFilteredIcons.length > 0 && (_jsx("div", { className: "uxpcore_icon-picker-modal__pagination", children: _jsx(PaginationComponent, { total: allFilteredIcons.length, pageSize: pageSize, page: page, onPageChange: setPage, onPageSizeChange: (newSize) => {
                                    setPageSize(newSize);
                                    setPage(1);
                                } }) }))] }), renderSidePanel()] }) }));
};
export default IconPickerModal;
